import { useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetchSse } from '@/hooks/useFetchSse';

import type { Turn } from './conversation';
import { Mascot } from './Mascot';

/** 面板與它的 router 依賴是另一個 chunk：沒打開過助理的人不下載。 */
const ChatPanel = lazy(() => import('./ChatPanel').then((m) => ({ default: m.ChatPanel })));

/**
 * 右下角的 AI 理財助理入口（ADR-0004）。掛在 __root.tsx 的 shell 裡、頁尾之後，
 * 所以每一頁都有。問答歷史與進行中的請求住在這裡（而不是面板裡）：一題要 25–45 秒，
 * 關掉面板繼續逛，答案照樣寫回來，入口的小芽在這段時間維持「思考中」。
 * 重新整理就消失——不留逐字稿是刻意的（B-D7）。
 */
export function ChatWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const sse = useFetchSse();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  // 全頁助理（/$market/assistant）本身就是同一個對話介面，再疊一顆浮動按鈕只會蓋住輸入列。
  const onAssistantPage = useRouterState({
    select: (s) => /^\/(tw|us)\/assistant\/?$/.test(s.location.pathname),
  });

  const close = useCallback(() => {
    setOpen(false);
    // 焦點還給觸發它的按鈕，鍵盤使用者才不會掉回 <body> 的最上面。
    buttonRef.current?.focus();
  }, []);

  if (onAssistantPage) return null;

  return (
    <>
      {open ? (
        <Suspense fallback={null}>
          <ChatPanel id={panelId} turns={turns} setTurns={setTurns} sse={sse} onClose={close} />
        </Suspense>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (open) close();
          else setOpen(true);
        }}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={open ? t('assistant.close') : t('assistant.open')}
        title={t('assistant.title')}
        data-testid="assistant-toggle"
        className={[
          // lg 以下 AI 助理是底部分頁列的一格，浮動按鈕只會蓋住內容。
          'fixed right-[calc(1rem+var(--safe-right))] bottom-[calc(1rem+var(--safe-bottom))] z-40',
          'hidden size-16 items-center justify-center lg:inline-flex',
          'rounded-full border border-border-1 bg-surface-1 text-fg-1 shadow-lg',
          'hover:bg-surface-2',
        ].join(' ')}
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <Mascot size="md" state={sse.active ? 'thinking' : 'idle'} decorative />
        )}
      </button>
    </>
  );
}
